import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getFunnel, checkout, subscribe as subscribeNewsletter, trackFunnel } from "@/lib/api";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, Lock, Leaf, Clock, Download, Infinity as InfinityIcon, ShieldCheck, Feather, Sparkles } from "lucide-react";

const isSub = (o) => o?.type === "subscription";

function priceLabel(o) {
  return isSub(o) ? `$${o.price}${o.price_suffix}` : `$${Number(o.price).toFixed(2)}`;
}

function useCountdown(endsAt) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const diff = new Date(endsAt).getTime() - Date.now();
      if (diff <= 0) { setLeft({ done: true }); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setLeft({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return left;
}

function CountdownBanner({ countdown }) {
  const left = useCountdown(countdown?.ends_at);
  if (!countdown || !left || left.done) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="bg-brand-charcoal text-brand-cream" data-testid="funnel-countdown">
      <div className="max-w-6xl mx-auto px-6 py-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center">
        <span className="font-sans text-[0.66rem] uppercase tracking-[0.16em] flex items-center gap-2"><Clock size={13} /> {countdown.message}</span>
        <span className="font-mono text-sm tabular-nums" data-testid="funnel-countdown-timer">
          {left.d > 0 && `${left.d}d `}{pad(left.h)}:{pad(left.m)}:{pad(left.s)}
        </span>
      </div>
    </div>
  );
}

function BookCard({ card, onBuy, loadingKey }) {
  if (!card) return null;
  const loading = loadingKey === card.lookup_key;
  return (
    <div className="bg-brand-warm-white border border-brand-linen overflow-hidden flex flex-col group" data-testid={`funnel-book-card-${card.slug}`}>
      <div className="aspect-[3/4] overflow-hidden bg-brand-cream">
        <img src={card.cover} alt={card.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
      </div>
      <div className="p-4 flex flex-col flex-1">
        {card.category && <span className="overline text-[0.54rem] text-brand-copper">{card.category}</span>}
        <h3 className="font-serif text-lg mt-1.5 leading-snug">{card.title}</h3>
        <p className="font-body text-brand-muted text-[0.8rem] mt-1.5 leading-relaxed flex-1 line-clamp-3">{card.tagline}</p>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-brand-linen">
          <span className="font-serif text-lg text-brand-charcoal">{priceLabel(card)}</span>
          <button onClick={() => onBuy(card)} disabled={loading} data-testid={`funnel-upsell-cta-${card.slug}`}
            className="inline-flex items-center gap-1.5 border border-brand-gold text-brand-charcoal px-4 py-2 font-sans text-[0.62rem] uppercase tracking-[0.12em] hover:bg-brand-olive hover:text-brand-cream hover:border-brand-olive transition-colors disabled:opacity-60">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />} Add
          </button>
        </div>
      </div>
    </div>
  );
}

function TrustBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2.5 justify-center">
      <Icon size={17} className="text-brand-sage shrink-0" strokeWidth={1.6} />
      <span className="font-sans text-[0.66rem] uppercase tracking-[0.13em] text-brand-body">{label}</span>
    </div>
  );
}

export default function FunnelLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [pendingCard, setPendingCard] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [source] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      let s = p.get("utm_source") || p.get("ref") || p.get("src");
      const medium = p.get("utm_medium");
      if (s && medium) s = `${s}/${medium}`;
      if (!s && document.referrer) {
        const h = new URL(document.referrer).hostname.replace(/^www\./, "");
        if (h && h !== window.location.hostname) s = h;
      }
      return (s || "direct").toLowerCase().slice(0, 40);
    } catch { return "direct"; }
  });

  useEffect(() => {
    setData(null); setNotFound(false);
    getFunnel(slug).then((d) => {
      setData(d);
      trackFunnel(slug, "view", d.served_variant, source);
    }).catch(() => setNotFound(true));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const buy = (card) => {
    setPendingCard(card);
    setCheckoutOpen(true);
    trackFunnel(slug, "cta_click", data?.served_variant || null, source);
  };

  const doCheckout = async (name, email) => {
    if (!pendingCard) return;
    setLoadingKey(pendingCard.lookup_key);
    try {
      const res = await checkout({ lookup_key: pendingCard.lookup_key, origin_url: window.location.origin,
        funnel_slug: slug, variant: data?.served_variant || "", source, name, email });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open checkout. Please try again."); setLoadingKey(null); }
  };

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try { await subscribeNewsletter({ email: email.trim() }); setSubscribed(true); setEmail(""); }
    catch { toast.error("Could not sign you up just now. Please try again."); }
  };

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-brand-cream">
      <h1 className="font-serif text-4xl mb-3">This offer has gone quiet</h1>
      <Link to="/" className="text-brand-copper underline font-body">Return to Rosewood Cottage</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-brand-cream"><Loader2 className="animate-spin text-brand-sage" /></div>;

  const o = data.offer;
  const digital = o.type === "digital";
  const bundleOffer = o.type === "bundle";
  const moreBooks = data.more_books || [];
  const includes = (o.includes || []).slice(0, 6);
  const toc = (o.toc || []).slice(0, 8);

  return (
    <div className="bg-brand-cream min-h-screen" data-testid="funnel-landing">
      <CountdownBanner countdown={data.countdown} />
      {/* minimal chrome */}
      <div className="border-b border-brand-linen">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-lg tracking-wide text-brand-charcoal flex items-center gap-2" data-testid="funnel-wordmark">
            <Leaf size={16} className="text-brand-sage" /> Rosewood Cottage
          </Link>
          <span className="font-sans text-[0.62rem] uppercase tracking-[0.16em] text-brand-muted flex items-center gap-2">
            <Lock size={12} /> Secure checkout
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-14 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="overline text-brand-copper">{bundleOffer ? "The complete collection" : isSub(o) ? "Join the cottage" : "A cottage edition"}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mt-4 mb-5 leading-[1.02]" data-testid="funnel-headline">{o.headline || o.title}</h1>
          <p className="font-body text-brand-body text-lg leading-relaxed max-w-xl">{o.tagline}</p>

          <ul className="mt-8 space-y-3" data-testid="funnel-benefits">
            {o.benefits.map((b, i) => (
              <li key={i} className="flex gap-3 font-body text-brand-body">
                <Check size={18} className="text-brand-sage shrink-0 mt-0.5" strokeWidth={2} /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap items-center gap-6">
            <button onClick={() => buy(o)} disabled={loadingKey === o.lookup_key} data-testid="funnel-primary-cta"
              className="inline-flex items-center gap-3 bg-brand-olive text-brand-cream px-9 py-4 font-sans text-xs uppercase tracking-[0.18em] hover:bg-brand-charcoal transition-colors disabled:opacity-60">
              {loadingKey === o.lookup_key ? <Loader2 size={16} className="animate-spin" /> : null}
              {o.cta} · {priceLabel(o)}
            </button>
          </div>
          <p className="mt-4 font-sans text-[0.68rem] uppercase tracking-[0.12em] text-brand-muted">{o.delivery}</p>
        </div>

        <div className="relative">
          <div className="overflow-hidden border border-brand-linen shadow-xl">
            <img src={o.cover} alt={o.title} className="w-full aspect-[4/3] object-cover" data-testid="funnel-hero-image" />
          </div>
          <div className="absolute -bottom-5 -right-5 bg-brand-cream border border-brand-linen px-6 py-4 shadow-lg">
            <span className="font-serif text-3xl text-brand-charcoal">{priceLabel(o)}</span>
            {digital && <p className="font-sans text-[0.6rem] uppercase tracking-[0.12em] text-brand-muted mt-1">One-off · yours forever</p>}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-brand-linen bg-brand-warm-white" data-testid="funnel-trust-strip">
        <div className="max-w-5xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
          <TrustBadge icon={Download} label="Instant delivery" />
          <TrustBadge icon={InfinityIcon} label="Yours forever" />
          <TrustBadge icon={Feather} label="Hand-set edition" />
          <TrustBadge icon={ShieldCheck} label="Secure Stripe checkout" />
        </div>
      </section>

      {/* Story / foreword — a word from Agnes */}
      {(o.foreword || o.story || o.description) && (
        <section className="bg-brand-cream" data-testid="funnel-story">
          <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
            <div className="text-center">
              <span className="overline text-brand-copper">A word from Agnes</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-light mt-3 mb-8">Why I set this down</h2>
            </div>
            {o.foreword && (
              <p className="font-body text-brand-body text-lg leading-loose first-letter:text-5xl first-letter:font-serif first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:text-brand-olive">
                {o.foreword}
              </p>
            )}
            {o.story && (
              <p className="font-body text-brand-body text-lg leading-loose mt-6">{o.story}</p>
            )}
            {!o.foreword && !o.story && o.description && (
              <p className="font-body text-brand-body text-lg leading-loose">{o.description}</p>
            )}
            <p className="font-serif text-xl italic text-brand-olive mt-8 text-right">— Agnes</p>
          </div>
        </section>
      )}

      {/* What's inside */}
      {(includes.length > 0 || toc.length > 0) && (
        <section className="border-y border-brand-linen bg-brand-warm-white" data-testid="funnel-inside">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-14">
            {includes.length > 0 && (
              <div>
                <span className="overline text-brand-copper">What's included</span>
                <h2 className="font-serif text-3xl font-light mt-3 mb-7">Inside these pages</h2>
                <ul className="space-y-4">
                  {includes.map((inc, i) => (
                    <li key={i} className="flex gap-3 font-body text-brand-body text-lg border-b border-brand-linen pb-4 last:border-0">
                      <Check size={19} className="text-brand-sage shrink-0 mt-1" strokeWidth={2} /> {inc}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {toc.length > 0 && (
              <div className="bg-brand-olive text-brand-cream p-9">
                <span className="overline text-brand-gold">Table of contents</span>
                <h2 className="font-serif text-3xl font-light text-brand-cream mt-3 mb-6">A glimpse of the chapters</h2>
                <ol className="space-y-4">
                  {toc.map((t, i) => (
                    <li key={i} className="flex gap-4 items-baseline border-b border-brand-cream/20 pb-4 last:border-0">
                      <span className="font-serif text-xl text-brand-gold w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-body text-brand-cream/90">{t}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Gentle urgency + repeat CTA */}
      <section className="bg-brand-cream" data-testid="funnel-urgency">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Sparkles size={22} className="text-brand-copper mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-serif text-2xl sm:text-3xl font-light leading-snug text-brand-charcoal">
            {digital || bundleOffer
              ? "Bring it home tonight and start reading before the kettle's even cool."
              : "Begin today and tomorrow's letter is already waiting for you."}
          </p>
          <p className="font-body text-brand-muted text-lg leading-relaxed mt-5 max-w-xl mx-auto">
            No shelf space needed, no waiting for the post — it arrives the moment you decide. And should you ever wish to gather the whole library, it costs far less taken together than one edition at a time.
          </p>
          <div className="mt-9">
            <button onClick={() => buy(o)} disabled={loadingKey === o.lookup_key} data-testid="funnel-secondary-cta"
              className="inline-flex items-center gap-3 bg-brand-olive text-brand-cream px-9 py-4 font-sans text-xs uppercase tracking-[0.18em] hover:bg-brand-charcoal transition-colors disabled:opacity-60">
              {loadingKey === o.lookup_key ? <Loader2 size={16} className="animate-spin" /> : null}
              {o.cta} · {priceLabel(o)}
            </button>
          </div>
        </div>
      </section>

      {/* Bundle spotlight */}
      {data.bundle && (
        <section className="bg-brand-olive text-brand-cream" data-testid="funnel-bundle-spotlight">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
            <div className="overflow-hidden border border-brand-cream/20 shadow-xl order-2 lg:order-1">
              <img src={data.bundle.cover} alt={data.bundle.title} className="w-full aspect-[4/3] object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="overline text-brand-gold">Best value — save the most</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-light text-brand-cream mt-3 mb-4 leading-tight">{data.bundle.title}</h2>
              <p className="font-body text-brand-cream/85 text-lg leading-relaxed">{data.bundle.tagline}</p>
              {data.bundle.subtitle && <p className="font-body text-brand-cream/70 mt-3">{data.bundle.subtitle}</p>}
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <span className="font-serif text-3xl text-brand-cream">{priceLabel(data.bundle)}</span>
                <button onClick={() => buy(data.bundle)} disabled={loadingKey === data.bundle.lookup_key} data-testid="funnel-bundle-cta"
                  className="inline-flex items-center gap-3 bg-brand-cream text-brand-charcoal px-8 py-3.5 font-sans text-xs uppercase tracking-[0.16em] hover:bg-brand-gold transition-colors disabled:opacity-60">
                  {loadingKey === data.bundle.lookup_key ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={15} />}
                  {data.bundle.cta || "Get the Bundle"}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All other ebooks — individual upsells */}
      {moreBooks.length > 0 && (
        <section className="bg-brand-warm-white border-t border-brand-linen" data-testid="funnel-more-books">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
            <div className="text-center mb-11">
              <span className="overline text-brand-copper">More from Agnes's library</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-light mt-3">Add a companion edition</h2>
              <p className="font-body text-brand-muted text-lg mt-3 max-w-xl mx-auto">Each one stands on its own — take whichever calls to you.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {moreBooks.map((c) => (
                <BookCard key={c.slug} card={c} onBuy={buy} loadingKey={loadingKey} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Email capture */}
      <section className="border-t border-brand-linen bg-brand-cream">
        <div className="max-w-2xl mx-auto px-6 py-14 text-center">
          <span className="overline text-brand-copper">Not quite ready?</span>
          <h2 className="font-serif text-3xl font-light mt-3 mb-3">Let me write to you</h2>
          <p className="font-body text-brand-muted leading-relaxed mb-6">Join the letter for a free recipe, seasonal wisdom, and word when new editions arrive. No noise — only the good, quiet things.</p>
          {subscribed ? (
            <p className="font-serif text-xl text-brand-olive italic" data-testid="funnel-subscribe-success">Thank you, dear — check your inbox.</p>
          ) : (
            <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="funnel-email-form">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="funnel-email-input"
                placeholder="your@email.com" className="flex-1 bg-brand-warm-white border border-brand-linen px-4 py-3 font-body focus:outline-none focus:border-brand-gold" />
              <button type="submit" data-testid="funnel-email-submit"
                className="bg-brand-olive text-brand-cream px-6 py-3 font-sans text-xs uppercase tracking-[0.14em] hover:bg-brand-charcoal transition-colors">
                Send it to me
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Legal / trust footer */}
      <footer className="bg-brand-cream border-t border-brand-linen">
        <div className="max-w-4xl mx-auto px-6 py-10 text-center">
          <p className="font-body text-sm text-brand-muted leading-relaxed">
            {digital || bundleOffer
              ? "Digital purchases are delivered instantly to your account and are non-refundable once access has been granted, as permitted by law."
              : "Subscriptions renew automatically and can be cancelled at any time from your account. No refunds for partial billing periods."}
          </p>
          <p className="font-body text-sm text-brand-muted mt-3">
            Payments are processed securely by Stripe. By purchasing you agree to our{" "}
            <Link to="/terms" className="text-brand-copper underline">Terms</Link>,{" "}
            <Link to="/refund-policy" className="text-brand-copper underline">Refund Policy</Link> and{" "}
            <Link to="/privacy" className="text-brand-copper underline">Privacy Policy</Link>.
          </p>
          <p className="font-sans text-[0.6rem] uppercase tracking-[0.16em] text-brand-muted mt-6">© Rosewood Cottage · Agnes Whitmore</p>
        </div>
      </footer>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} onConfirm={doCheckout}
        submitting={!!loadingKey} title={pendingCard ? pendingCard.title || "Complete your order" : "Complete your order"} />
    </div>
  );
}
